// Global Instructions Rule Applied!
//
// The Tiers strip of the distribution form (waterfall phase 6 PR 2b, owner
// 2026-10-08): one line per tier of the waterfall, above the Allocation
// grid, so the calculation reads the way the waterfall works - a bucket per
// tier, filled from the lowest up, overflowing into the next once its hurdle
// is met. Each line says the tier as the agreement states it (the Terms
// tab's wording, waterfallTierWording.ts), what the run tested (the target,
// what had been received before), what this distribution puts under the tier
// and the bucket's state after it.
//
// Everything is the server's (wf__distribution_tiers, from the
// distribution's frozen snapshot): on the fund basis the figures are the
// class test the run made, on the commitment basis the sum over the
// partners. A distribution computed before the tier tests were kept has no
// states; the strip then says so instead of guessing.

import React from 'react'
import { Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import NumberText from '../../../../core/components/NumberText.tsx'
import type { DistributionTierStrip } from '../../../../state_manager/data_models/distribution_detail.ts'
import {
  describeTierTerms,
  TIER_GP_CLASS,
  TIER_METRIC_PROFIT_SHARE
} from '../../../funds/components/forms/tabs/waterfallTierWording.ts'
import { hasFigure, tierStateTag, tierTitle } from './allocationLabels.ts'

const { Text } = Typography

// lookup labels the strip keys behaviour on (cfg.waterfall_stop_metrics / _bases)
const METRIC_NONE = 'None'
const BASE_TIER_GROUP = 'Distributions under tier group'
const BASIS_FUND = 'fund'

interface TiersStripProps {
  tiers: DistributionTierStrip[]
  /** the basis the tiers were tested on (cfg.waterfall_bases.code) and its label */
  basis: string | null | undefined
  basisLabel: string | null | undefined
  /** how earlier distributions were checked (cfg.waterfall_history_checks.label) */
  historyCheckLabel: string | null | undefined
  /** an Issued distribution cannot be recomputed */
  readOnly: boolean
}

/** The tier in words, from the definition frozen in the distribution's snapshot. */
export function describeStripTier(tier: DistributionTierStrip): string {
  const gp = tier.splits.find((s) => s.class === TIER_GP_CLASS)
  return describeTierTerms({
    gpSource: gp?.source ? { code: gp.source, label: gp.source_label ?? gp.source } : null,
    gpSplitPercent: gp?.split_percent,
    isNone: tier.metric === METRIC_NONE,
    metricLabel: tier.metric,
    baseLabel: tier.base,
    stopClassLabel: tier.stop_class,
    thresholdSource: tier.threshold_source
      ? { code: tier.threshold_source, label: tier.threshold_source_label ?? tier.threshold_source }
      : null,
    stopThreshold: tier.stop_threshold,
    stopTierGroup: tier.stop_tier_group,
    isTierGroupBase: tier.base === BASE_TIER_GROUP,
    // the Cross-Pool Loss Offset tier of a waterfall with pools
    ...(tier.prog__is_loss_offset ? { lossOffsetPools: tier.offsets_pools } : {})
  })
}

export default function TiersStrip({ tiers, basis, basisLabel, historyCheckLabel, readOnly }: TiersStripProps) {
  if (tiers.length === 0) return null
  const hasStates = tiers.some((t) => t.state_after != null)

  const columns: ColumnsType<DistributionTierStrip> = [
    {
      title: 'Tier',
      key: 'tier',
      width: 200,
      render: (_: unknown, t) => tierTitle(t.tier_sequence, t.tier_type_label)
    },
    {
      title: 'As the agreement reads',
      key: 'reads',
      render: (_: unknown, t) => <span data-testid={`strip-tier-reads-${t.tier_sequence}`}>{describeStripTier(t)}</span>
    },
    {
      title: 'Target',
      key: 'target',
      width: 170,
      align: 'right',
      render: (_: unknown, t) =>
        t.metric === TIER_METRIC_PROFIT_SHARE ? (
          t.catch_up == null ? (
            <Text type='secondary'>—</Text>
          ) : (
            <span>
              <Text type='secondary'>catch-up </Text>
              <NumberText value={t.catch_up} />
            </span>
          )
        ) : (
          <NumberText value={t.target} blank={<Text type='secondary'>—</Text>} />
        )
    },
    {
      title: 'Received before',
      dataIndex: 'received_before',
      width: 150,
      align: 'right',
      render: (v: number | null | undefined, t) => (
        <NumberText
          value={t.metric === TIER_METRIC_PROFIT_SHARE ? t.catch_up_received : v}
          blank={<Text type='secondary'>—</Text>}
        />
      )
    },
    {
      title: 'This distribution',
      dataIndex: 'this_distribution',
      width: 150,
      align: 'right',
      render: (v: number, t) => (
        <div>
          <NumberText value={v} wrap={(f) => <Text strong>{f}</Text>} />
          {/* the Cross-Pool Loss Offset tier: the pools whose losses it offset */}
          {t.remedied.map((r) => (
            <div key={r.pool_id}>
              <Text type='secondary' className='text-xs'>
                <NumberText value={r.amount} /> to {r.pool_code ?? 'another pool'}
              </Text>
            </div>
          ))}
        </div>
      )
    },
    { title: 'To partners', dataIndex: 'to_partners', width: 150, align: 'right', render: (v: number) => <NumberText value={v} /> },
    { title: 'Carry', dataIndex: 'carry', width: 140, align: 'right', render: (v: number) => <NumberText value={v} /> },
    {
      title: 'State after',
      key: 'state',
      width: 190,
      render: (_: unknown, t) => {
        if (t.state_after == null) return <Text type='secondary'>—</Text>
        const tag = tierStateTag(t.state_after)
        if (!tag) return null
        return (
          <Tag color={tag.color}>
            {t.state_after === 'open' && hasFigure(t.remaining) ? (
              <>
                <NumberText value={t.remaining} /> {tag.label}
              </>
            ) : t.state_after === 'open' ? (
              'Open'
            ) : (
              tag.label
            )}
          </Tag>
        )
      }
    }
  ]

  return (
    <div className='mb-3'>
      <Text strong>Tiers</Text>
      <Table<DistributionTierStrip>
        size='small'
        rowKey='tier_id'
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={tiers}
        className='mt-1'
      />
      <Text type='secondary' className='block mt-1'>
        {basis === BASIS_FUND
          ? 'Tested on the fund basis: the target and what was received before are the class of partners in aggregate, as the run tested them.'
          : `Tested on the ${(basisLabel ?? 'commitment').toLowerCase()} basis: every partner on its own state; the figures here are the sum over the partners.`}
        {historyCheckLabel ? ` Earlier distributions checked: ${historyCheckLabel}.` : ''}
        {hasStates
          ? ''
          : readOnly
            ? ' This distribution was computed before the tier tests were kept, so the buckets show no state.'
            : ' This distribution was computed before the tier tests were kept, so the buckets show no state - Recompute to see them.'}
      </Text>
    </div>
  )
}
